import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import { featuredComics, latestUpdates } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = featuredComics.find((c) => c.id === id);
  const latestComic = latestUpdates.find((c) => c.id === id);

  const displayComic = comic || (latestComic ? {
    ...latestComic,
    author: '未知作者',
    category: 'fantasy',
    categoryName: '奇幻冒险',
    rating: 9.0,
    views: '500K',
    status: '连载中',
    isFree: latestComic.isFree,
    description: `${latestComic.title}，精彩连载中，欢迎在线观看。`,
  } : null);

  if (!displayComic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-4xl font-black text-white mb-4">漫画未找到</h1>
            <p className="text-gray-400 mb-6">该漫画可能已被移除或链接无效</p>
            <Link to="/" className="text-comic-primary hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = featuredComics.filter((c) => c.id !== displayComic.id).slice(0, 4);

  return (
    <>
      <SEOHead
        title={`${displayComic.title} - 韩国漫画网免费在线漫画阅读`}
        description={`${displayComic.title}：${displayComic.description}作者：${displayComic.author}，评分${displayComic.rating}分。韩国漫画网提供${displayComic.title}免费在线高清漫画阅读。`}
        keywords={`${displayComic.title},${displayComic.author},${displayComic.categoryName}韩漫,在线漫画,免费韩漫,韩国漫画`}
        canonicalUrl={`http://w0ap.com/comic/${displayComic.id}`}
        breadcrumbs={[
          { label: '首页', path: '/' },
          { label: displayComic.categoryName, path: `/category/${displayComic.category}` },
          { label: displayComic.title },
        ]}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${displayComic.title} - 韩国漫画网`,
          description: displayComic.description,
          author: {
            '@type': 'Person',
            name: displayComic.author,
          },
          url: `http://w0ap.com/comic/${displayComic.id}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '韩国漫画网',
            url: 'http://w0ap.com/',
          },
        }}
      />

      <Navbar />

      <main>
        {/* 详情Hero */}
        <section className="relative h-[70vh] min-h-[500px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={displayComic.imageUrl || displayComic.thumbnailUrl}
              alt={displayComic.title}
              title={`${displayComic.title} - 韩国漫画网漫画详情`}
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-comic-dark via-comic-dark/80 to-comic-dark/30"></div>

          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-12 md:pb-16">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
                <div className="w-32 h-44 md:w-40 md:h-56 rounded-xl overflow-hidden border-2 border-comic-accent/40 flex-shrink-0 shadow-lg shadow-comic-accent/10">
                  <img
                    src={displayComic.thumbnailUrl || displayComic.imageUrl}
                    alt={`${displayComic.title}封面`}
                    title={`${displayComic.title} - 漫画封面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <Link
                      to={`/category/${displayComic.category}`}
                      className="px-3 py-1 rounded-full text-xs border border-comic-border text-gray-300 hover:border-comic-accent hover:text-comic-accent transition-colors"
                    >
                      {displayComic.categoryName}
                    </Link>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      displayComic.status === '连载中' ? 'bg-comic-accent/20 text-comic-accent' : 'bg-comic-cyan/20 text-comic-cyan'
                    }`}>
                      {displayComic.status}
                    </span>
                    <span className="flex items-center gap-1 text-comic-secondary">
                      <i className="ri-star-fill text-sm"></i>
                      <span className="text-white font-bold text-lg">{displayComic.rating}</span>
                    </span>
                  </div>
                  <h1 className="text-3xl md:text-5xl font-black text-white mb-2">{displayComic.title}</h1>
                  <p className="text-gray-400 text-sm mb-4">作者：{displayComic.author} · {displayComic.latestChapter}</p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/comic/${displayComic.id}/read`}
                      className="px-8 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white font-semibold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                    >
                      <i className="ri-book-open-line"></i>
                      开始阅读
                    </Link>
                    <button className="w-12 h-12 flex items-center justify-center rounded-full border border-comic-border text-gray-400 hover:text-comic-accent hover:border-comic-accent transition-all cursor-pointer" aria-label="收藏">
                      <i className="ri-heart-line text-lg"></i>
                    </button>
                    <button className="w-12 h-12 flex items-center justify-center rounded-full border border-comic-border text-gray-400 hover:text-comic-accent hover:border-comic-accent transition-all cursor-pointer" aria-label="分享">
                      <i className="ri-share-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 面包屑 */}
        <BreadcrumbNav
          items={[
            { label: '首页', path: '/' },
            { label: displayComic.categoryName, path: `/category/${displayComic.category}` },
            { label: displayComic.title },
          ]}
        />

        {/* 作品信息区 */}
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <span className="inline-block px-3 py-1 rounded-full text-xs border border-comic-border text-gray-400 mb-4">
                  {displayComic.categoryName}
                </span>
                <h2 className="text-2xl md:text-3xl font-black text-white mb-1">
                  作品简介
                </h2>
                <p className="text-gray-500 text-sm mb-6">{displayComic.author} 作品</p>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-8">
                  {displayComic.description}
                </p>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-6">
                  在这个精彩的故事中，{displayComic.author}以其独特的笔触和想象力，为读者勾勒出一个令人沉浸的世界。每一话都扣人心弦，角色塑造鲜明立体，情节发展出人意料又合情合理。无论是细腻的情感描绘，还是宏大场景的视觉冲击力，都展现出韩漫独有的艺术魅力。
                </p>
                <Link
                  to={`/comic/${displayComic.id}/read`}
                  className="text-comic-primary text-sm font-medium hover:underline inline-flex items-center gap-1"
                >
                  <i className="ri-book-read-line"></i>
                  继续阅读最新章节
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="lg:col-span-7">
                <div className="flex items-center justify-end gap-10 mb-8">
                  <div className="text-right">
                    <span className="block text-4xl md:text-5xl font-black text-white">{displayComic.rating}</span>
                    <span className="text-gray-500 text-xs">评分</span>
                  </div>
                  <div className="text-right">
                    <span className="block text-4xl md:text-5xl font-black text-white">{displayComic.views}</span>
                    <span className="text-gray-500 text-xs">阅读量</span>
                  </div>
                </div>

                <div className="relative">
                  <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden border border-comic-border relative z-20">
                    <img
                      src={displayComic.thumbnailUrl || displayComic.imageUrl}
                      alt={`${displayComic.title}截图1`}
                      title={`${displayComic.title} - 漫画内页`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute top-8 left-16 md:left-20 w-56 h-72 md:w-64 md:h-80 rounded-2xl overflow-hidden border border-comic-border z-10">
                    <img
                      src={displayComic.imageUrl || displayComic.thumbnailUrl}
                      alt={`${displayComic.title}截图2`}
                      title={`${displayComic.title} - 漫画内页`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 读者评论 */}
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 comic-dot-pattern">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="text-2xl md:text-4xl font-black text-white mb-8">
              精选<span className="text-comic-primary">评论</span>
            </h2>

            <div className="flex flex-col gap-6">
              <article className="bg-[#06060A] rounded-3xl p-6 md:p-8 flex flex-col lg:flex-row gap-6 items-start relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full speed-line opacity-20 pointer-events-none"></div>
                <div className="flex-shrink-0 relative z-10">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden border border-comic-accent/30">
                    <img
                      src="https://readdy.ai/api/search-image?query=Anime%20style%20young%20character%20portrait%20avatar%2C%20stylish%20look%2C%20soft%20warm%20lighting%2C%20neutral%20gray%20background%2C%20clean%20illustration%20style%2C%20square%20format&width=200&height=200&seq=avatar-review-1&orientation=squarish"
                      alt="漫迷小宇"
                      title="漫迷小宇 - 韩国漫画网读者"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="flex-1 relative z-10">
                  <p className="text-gray-200 text-sm leading-relaxed mb-4">
                    追了半年多了，画风越画越好，剧情节奏把控得很到位！{displayComic.author}老师真的太会讲故事了，每一话结尾都让人迫不及待想看下一话。
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold text-sm">漫迷小宇</p>
                      <p className="text-gray-500 text-xs">追更中</p>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-comic-secondary text-xs"></i>
                      ))}
                    </div>
                  </div>
                </div>
              </article>

              <article className="bg-[#06060A] rounded-3xl p-6 md:p-8 flex flex-col lg:flex-row gap-6 items-start relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full speed-line opacity-20 pointer-events-none"></div>
                <div className="flex-shrink-0 relative z-10">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden border border-comic-accent/30">
                    <img
                      src="https://readdy.ai/api/search-image?query=Anime%20style%20young%20female%20character%20portrait%20avatar%2C%20cute%20look%2C%20soft%20warm%20lighting%2C%20neutral%20gray%20background%2C%20clean%20illustration%20style%2C%20square%20format&width=200&height=200&seq=avatar-review-2&orientation=squarish"
                      alt="樱花之恋"
                      title="樱花之恋 - 韩国漫画网读者"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="flex-1 relative z-10">
                  <p className="text-gray-200 text-sm leading-relaxed mb-4">
                    朋友推荐的，一看就停不下来！角色之间的情感描写特别细腻，让我想起了自己的校园时光。已经推荐给好几个朋友了，大家一起追更超开心的～
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold text-sm">樱花之恋</p>
                      <p className="text-gray-500 text-xs">已追80话</p>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-comic-secondary text-xs"></i>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div className="text-center mt-8">
              <button className="px-8 py-3 rounded-full bg-comic-accent/10 border border-comic-accent/30 text-comic-accent font-semibold text-sm hover:bg-comic-accent/20 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-edit-line mr-2"></i>
                写长评
              </button>
            </div>
          </div>
        </section>

        {/* 相关推荐 */}
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-2xl md:text-4xl font-black text-white">
                同好也<span className="text-comic-primary">在看</span>
              </h2>
              <p className="text-gray-500 text-sm hidden md:block">相关推荐 · 更多精彩</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {relatedComics.map((related) => (
                <Link key={related.id} to={`/comic/${related.id}`} className="group cursor-pointer">
                  <article className="bg-comic-card rounded-2xl overflow-hidden transition-all duration-300 hover:bg-comic-card-hover h-full">
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={related.thumbnailUrl || related.imageUrl}
                        alt={related.title}
                        title={`${related.title} - 韩国漫画网相关推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2.5 left-2.5">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          related.status === '连载中' ? 'bg-comic-accent text-white' : 'bg-comic-cyan text-black'
                        }`}>
                          {related.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pt-8">
                        <h3 className="text-white font-bold text-sm mb-1 group-hover:text-comic-primary transition-colors">
                          {related.title}
                        </h3>
                        <p className="text-gray-300 text-xs">{related.categoryName}</p>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: `${displayComic.title}是免费的吗？`,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: displayComic.isFree
                      ? `是的，《${displayComic.title}》在韩国漫画网提供免费在线阅读，无需付费即可畅享全部章节。`
                      : `《${displayComic.title}》部分章节为付费内容，但韩国漫画网提供了大量免费章节供读者体验。`,
                  },
                },
                {
                  '@type': 'Question',
                  name: `${displayComic.title}多久更新一次？`,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: displayComic.status === '已完结'
                      ? `《${displayComic.title}》已经完结，所有章节均可阅读。`
                      : `《${displayComic.title}》为连载作品，韩国漫画网每日同步更新，确保读者第一时间看到最新章节。`,
                  },
                },
                {
                  '@type': 'Question',
                  name: `${displayComic.title}适合什么年龄段的读者？`,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: `《${displayComic.title}》属于${displayComic.categoryName}类型，适合喜欢该题材的漫画爱好者阅读。韩国漫画网建议读者根据个人兴趣选择合适的作品。`,
                  },
                },
              ],
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}